import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  UsersIcon,
  PencilIcon,
  TrashIcon,
  StopIcon,
  EyeIcon,
  UserPlusIcon,
  AcademicCapIcon,
  RectangleGroupIcon,
} from "@heroicons/react/24/outline";

import { Card, CardHeader, CardTitle, CardContent } from "./ui/Card";
import { Button } from "./ui/Button";
import { Badge } from "./ui/Badge";
import { Modal } from "./ui/Modal";
import { RoleGuard } from "./ui/RoleGuard";
import { apiService } from "../services/api";
import { formatLocalDate, formatTime } from "../utils/helpers";

const SessionCard = ({
  session,
  onEdit,
  onDelete,
  onAddSkills,
  onAddGroups,
  availablePlayers,
  availableStaff,
  evaluationType = "skills",
}) => {
  const navigate = useNavigate();
  const { clubId, evalId } = useParams();
  const [loading, setLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "green";
      case "completed":
        return "blue";
      case "scheduled":
        return "yellow";
      case "cancelled":
        return "red";
      default:
        return "gray";
    }
  };

  const handleCheckIn = () => {
    navigate(
      `/portal/club/${clubId}/evaluation/${evalId}/session/${session.id}/checkin`,
    );
  };

  const handleStatusChange = async (newStatus) => {
    setLoading(true);
    try {
      await apiService.updateSession(session.id, {
        evaluation_id: evalId,
        status: newStatus,
      });
      toast.success(`Session ${newStatus}!`);
      // Trigger parent refresh
      window.location.reload();
    } catch (error) {
      toast.error("Failed to update session status");
    }
    setLoading(false);
  };

  return (
    <>
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-lg">{session.name}</CardTitle>
              <div className="flex items-center space-x-2 mt-1">
                <Badge variant={getStatusColor(session.status)}>
                  {session.status.toUpperCase()}
                </Badge>
                <span className="text-sm text-gray-500">
                  {session.checkin_count || 0} checked in
                </span>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDetailsModal(true)}
            >
              <EyeIcon className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center text-sm text-gray-600">
              <CalendarDaysIcon className="w-4 h-4 mr-2" />
              <span>{formatLocalDate(session.session_date)}</span>
            </div>

            {(session.start_time || session.end_time) && (
              <div className="flex items-center text-sm text-gray-600">
                <ClockIcon className="w-4 h-4 mr-2" />
                <span>
                  {session.start_time && formatTime(session.start_time)}
                  {session.start_time && session.end_time && " - "}
                  {session.end_time && formatTime(session.end_time)}
                </span>
              </div>
            )}

            {session.location && (
              <div className="flex items-center text-sm text-gray-600">
                <MapPinIcon className="w-4 h-4 mr-2" />
                <span>{session.location}</span>
              </div>
            )}

            <div className="flex items-center text-sm text-gray-600">
              <UsersIcon className="w-4 h-4 mr-2" />
              <span>
                {session.checkin_count || 0} players •{" "}
                {session.staff_count || 0} staff
              </span>
            </div>

            {/* Skill Categories */}
            {session.session_skill_categories &&
              session.session_skill_categories.length > 0 && (
                <div className="flex items-start text-sm text-gray-600">
                  <AcademicCapIcon className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="flex flex-wrap gap-1">
                    {session.session_skill_categories.map((ssc) => (
                      <span
                        key={ssc.category_id}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: `${ssc.skill_categories?.color || "#6B7280"}20`,
                          color: ssc.skill_categories?.color || "#6B7280",
                        }}
                      >
                        {ssc.skill_categories?.name || "Unknown"}
                      </span>
                    ))}
                  </div>
                </div>
              )}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-wrap gap-2">
              {(session.status === "scheduled" ||
                session.status === "active") && (
                <Button size="sm" onClick={handleCheckIn} disabled={loading}>
                  <UserPlusIcon className="w-4 h-4 mr-1" />
                  Check In
                </Button>
              )}

              <RoleGuard roles={["super_admin", "club_admin", "coach"]}>
                {session.status !== "completed" &&
                  evaluationType === "skills" &&
                  onAddSkills && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onAddSkills(session)}
                      disabled={loading}
                    >
                      <AcademicCapIcon className="w-4 h-4 mr-1" />
                      Add Skills
                    </Button>
                  )}

                {session.status !== "completed" &&
                  evaluationType === "groups" &&
                  onAddGroups && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onAddGroups(session)}
                      disabled={loading}
                    >
                      <RectangleGroupIcon className="w-4 h-4 mr-1" />
                      Add Groups
                    </Button>
                  )}
                <RoleGuard roles={["super_admin", "club_admin"]}>
                  {session.status === "active" && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleStatusChange("completed")}
                      disabled={loading}
                    >
                      <StopIcon className="w-4 h-4 mr-1" />
                      Complete
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onEdit(session)}
                  >
                    <PencilIcon className="w-4 h-4 mr-1" />
                    Edit
                  </Button>

                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => onDelete(session)}
                  >
                    <TrashIcon className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </RoleGuard>
              </RoleGuard>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Session Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title={session.name}
        size="lg"
      >
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">
                Session Details
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span>{formatLocalDate(session.session_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time:</span>
                  <span>
                    {session.start_time && formatTime(session.start_time)}
                    {session.start_time && session.end_time && " - "}
                    {session.end_time && formatTime(session.end_time)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Location:</span>
                  <span>{session.location || "Not specified"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <Badge variant={getStatusColor(session.status)}>
                    {session.status.toUpperCase()}
                  </Badge>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-2">Participation</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Checked In:</span>
                  <span>{session.checkin_count || 0} players</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Staff Assigned:</span>
                  <span>
                    {session.staff_count || 0} member
                    {session.staff_count === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Max Capacity:</span>
                  <span>{session.max_participants || "Unlimited"}</span>
                </div>
              </div>
            </div>
          </div>

          {session.notes && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
              <div className="bg-gray-50 rounded-md p-3">
                <p className="text-sm text-gray-700">{session.notes}</p>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export { SessionCard };
